import "@fontsource/roboto";
import "@fontsource/roboto/500.css";
import { ThemeProvider } from "@mui/material";

import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import AllServices from "./app/allServices";
import CarService from "./app/carService";
import ProductCart from "./app/cart";
import ClientSupport from "./app/clientSupport";
import EcommercePage from "./app/ecommerce";
import EducationalCity from "./app/educationalCity";
import ApplicationRequest from "./app/educationalCity/ApplicationRequest";
import EducationalCityDetails from "./app/educationalCity/Details";
import HelpCenter from "./app/help";
import HomePage from "./app/home";
import Pricing from "./app/pricing";
import ProviderServices from "./app/providerServices";
import Message from "./components/message";
import ProductDetail from "./components/productDetail";
import theme from "./components/theme";
import { AUTH_ROUTE, DASHBOARD_ROUTES } from "./routes";
import { GuestLayout } from "./routes/GuestLayout";
import { ProtectedLayout } from "./routes/ProtectedLayout";
import AllProviders from "./app/providers";
import CompanyDetail from "./app/companyDetail";
import SelectTime from "./app/providerServices/SelectServiceTime";
import SendRequest from "./app/providerServices/SendRequest";
import { SnackbarProvider } from "./lib/context/snackbarContext";
import ProductCheckout from "./app/checkout";
import UserCart from "./app/user/ecommerce/Cart";
import PurchaseInvoice from "./app/user/ecommerce/PurchaseInvoice";
import OrderDetailPage from "./app/user/ecommerce/OrderDetail";
import CleaningService from "./app/cleaning";
import BookAppointmentPage from "./app/appointment";
import ServiceDetailPage from "./app/user/services/ServiceDetail";
import PrivacyPolicy from "./app/privacyPolicy";
import TermsAndConditions from "./app/terms&Conditions";
import ReturnPolicy from "./app/returnPolicy";
import { useEffect, useRef } from "react";
import AboutUsPage from "./app/aboutUs";
import CategoryProductPage from "./app/categoryProducts";
import SearchProductPage from "./app/searchProduct";
import JoinWaitlist from "./app/joinWaitlist";
import ConsumerProtection from "./app/consumerProtection";
import CookiePolicy from "./app/cookiePolicy";
import Faqs from "./app/help/Faqs";
import DisputeResolution from "./app/disputeResolution";
import ContactUs from "./app/contactUS";
import InsurancePolicy from "./app/insurancePolicy";
import WorkerGuidancePolicy from "./app/workerGuidancePolicy";
import ComingSoon from "./app/comingSoon";
import ContactSupport from "./app/help/clientHelp";
import PackageDetailPage from "./app/packageDetail";
import CustomPackages from "./app/customPackages";
import englishJson from "./utils/locales/en/english.json";
import romaninanJson from "./utils/locales/ro/romanian.json";

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";
import usePageTracking from "./hooks/usePageTracking";
import AllBlogs from "./app/blogs";
import BlogDetail from "./app/blogs/Detail";

ReactGA.initialize("G-GYJP3XRZCD"); // Replace with your GA4 Measurement ID

function App() {
  const location = useLocation();
  usePageTracking();
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const requestGeolocation = () => {
      if (!navigator.geolocation) {
        console.warn("Geolocation is not supported by this browser.");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          localStorage.setItem("latitude", latitude.toString());
          localStorage.setItem("longitude", longitude.toString());
          localStorage.setItem("geolocationChoice", "allowed");
        },
        (error) => {
          console.error("Error fetching location:", error.message);

          if (error.code === error.PERMISSION_DENIED) {
            localStorage.setItem("geolocationChoice", "Never allowed");
          }
        }
      );
    };

    requestGeolocation();
  }, []);

  i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
      // the translations
      // (tip move them in a JSON file and import them,
      // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
      resources: {
        en: {
          translation: englishJson[0],
        },
        ro: {
          translation: romaninanJson[0],
        },
      },
      lng: currentLanguage, // if you're using a language detector, do not define the lng option
      fallbackLng: "en",

      interpolation: {
        escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
      },
    });

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <Routes>
          {/* Public Routes */}
          <Route path={"/"} element={<EcommercePage />} />
          <Route path={"/cleaning-services"} element={<HomePage />} />
          <Route path={"/car-services"} element={<HomePage />} />
          <Route path={"/custom-packages"} element={<CustomPackages />} />
          <Route path={"/education-city"} element={<EducationalCity />} />
          <Route path={"/pricing"} element={<Pricing />} />
          <Route path={"/help-center"} element={<HelpCenter />} />
          <Route path={"/faqs/:type"} element={<Faqs />} />
          <Route path={"/help-center/:type"} element={<ClientSupport />} />
          <Route path={"/message"} element={<Message />} />
          <Route path={"/blogs"} element={<AllBlogs />} />
          <Route path={"/blogs/:slug"} element={<BlogDetail />} />
          {/* <Route path={"/join-waitlist"} element={<JoinWaitlist />} /> */}
          <Route path={"/contact/support"} element={<ContactSupport />} />
          {/* Service Page */}
          <Route path={"/all-service"} element={<AllServices />} />
          {/* <Route path={"/e-commerce"} element={<EcommercePage />} /> */}
          <Route path={"/car-service"} element={<CarService />} />
          <Route path={"/cleaning-service"} element={<CleaningService />} />
          {/* Detail Page */}
          <Route
            path={"/education-city/:id"}
            element={<EducationalCityDetails />}
          />
          <Route path={"/product/:url"} element={<ProductDetail />} />
          <Route path={"/package/:url"} element={<PackageDetailPage />} />
          <Route
            path={"/category/:categoryId"}
            element={<CategoryProductPage />}
          />
          <Route path={"/search-product"} element={<SearchProductPage />} />

          <Route path={"/worker-detail/:id"} element={<CompanyDetail />} />
          <Route path={"/coming-soon"} element={<ComingSoon />} />

          {/* Appointment Book Pages */}
          <Route
            path={"/book-appointment/:workerId/:serviceId"}
            element={<BookAppointmentPage />}
          />
          {/* Application form Request */}
          <Route
            path={"/education-city/:id/form"}
            element={<ApplicationRequest />}
          />
          {/* Product Cart Page */}
          <Route path={"/cart"} element={<ProductCart />} />
          {/* Product Checkout Page */}
          <Route path={"/check-out"} element={<ProductCheckout />} />
          {/* Provider Services */}
          <Route path={"/service/:id"} element={<AllProviders />} />
          <Route
            path={"/all-service/:id/services"}
            element={<ProviderServices />}
          />
          <Route
            path={"/all-service/:id/services/time"}
            element={<SelectTime />}
          />
          <Route
            path={"/all-service/:id/check-out"}
            element={<SendRequest />}
          />
          {/* footer Route */}
          <Route path={"/privacy-policy"} element={<PrivacyPolicy />} />
          <Route path={"/terms-conditions"} element={<TermsAndConditions />} />
          <Route
            path={"/consumer-protection"}
            element={<ConsumerProtection />}
          />
          <Route path={"/contact-us"} element={<ContactUs />} />
          <Route path={"/cookie-policy"} element={<CookiePolicy />} />
          <Route path={"/dispute-resolution"} element={<DisputeResolution />} />
          <Route path={"/insurance-policy"} element={<InsurancePolicy />} />
          <Route
            path={"/worker-guidance-policy"}
            element={<WorkerGuidancePolicy />}
          />
          <Route path={"/return-policy"} element={<ReturnPolicy />} />
          {/* // do not uncommit */}
          <Route path={"/about-us"} element={<AboutUsPage />} />
          {/* Worker Detail */}
          {/* Authentication Routes */}
          <Route element={<GuestLayout />}>
            {AUTH_ROUTE.map(({ component, path }, index) => (
              <Route key={index} path={path} element={component} />
            ))}
          </Route>
          {/* Dashboard Routes */}
          <Route element={<ProtectedLayout />}>
            {DASHBOARD_ROUTES.map(({ component, path }, index) => (
              <Route key={index} path={path} element={component} />
            ))}
            <Route path={"/dashboard/e-commerce/cart"} element={<UserCart />} />
            <Route
              path={"/dashboard/e-commerce/orders"}
              element={<PurchaseInvoice />}
            />
            <Route
              path={"/dashboard/e-commerce/orders/:orderId"}
              element={<OrderDetailPage />}
            />
            <Route
              path={"/dashboard/service/detail/:appointmentId"}
              element={<ServiceDetailPage />}
            />
          </Route>
        </Routes>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
